// Submit Content page - displays outstanding and completed deliverables for content submission

import DOMPurify from "dompurify";
import { ContentDetailView } from "@/components/content/content-detail-view";
import { extractContentMedia } from "@/components/content/content-preview";
import {
  ApprovedBadge,
  ChangesRequestedBadge,
  CompletedBadge,
  ExtraContentBadge,
  PendingApprovalBadge,
} from "@/components/content/content-status-badge";
import { ExtraContentUpload } from "@/components/content/extra-content-upload";
import {
  getUnlinkedInstagramUsernames,
  isInstagramInsightsNotLinked,
} from "@/components/content/instagram-insights-banner";
import { InstagramStoryModal } from "@/components/content/instagram-story-modal";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import { captureEvent } from "@/lib/analytics-loader";
import {
  ContentState,
  extraContentKeys,
  filterContentRequirements,
  getButtonTitle,
  getContentDisplayName,
  getPostTypeDisplayName,
  isAwaitingReview,
  isInstagramStories,
  useContentRequirementsQuery,
  useExtraContentQuery,
  useExtraPostsQuery,
  useInstagramStoriesQuery,
  useRefetchInstagramStories,
  type ContentRequirement,
  type ExtraContent,
  type ExtraPost,
  type InstagramStory,
} from "@/lib/queries/content";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
} from "@/lib/queries/projects";
import {
  useBasicTermsQuery,
  useStandardBriefEnhancementsQuery,
  useDeleteYouTubeBrandConnectVideoCollabMutation,
} from "@/lib/queries/terms";
import { extractProjectIdAndToken } from "@/lib/url-utils";
import { Button, Card, CardContent, Skeleton, toast } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { first } from "lodash";
import {
  Check,
  ChevronRight,
  Clock,
  ExternalLink,
  FileImage,
  Gift,
  Image,
  Link2,
  Upload,
  Video,
} from "lucide-react";
import type { ReactNode, SyntheticEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CampaignRouteContext } from "@/lib/types/route-context";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/submit-content/$interactionId/",
)({
  component: SubmitContentPage,
});

// Narrowed shape of the fields shouldOpenStoryPickerDirectly reads from a
// ContentRequirement. Any ContentRequirement is structurally assignable to it.
interface StoryClickRequirement {
  readonly state?: string;
  readonly deliverable?: {
    readonly postType?: string | null;
    readonly requiresApproval?: boolean;
  };
}

// Shortcut only for IG Stories that don't require approval: open the story
// picker directly since there is no prior submission to reference. Approved
// stories fall through to the form page so the creator can see the approved
// media and overlay caption before picking the live story.
export function shouldOpenStoryPickerDirectly(
  requirement: StoryClickRequirement,
  requireInstagramInsights: boolean,
): boolean {
  const postType = requirement.deliverable?.postType;
  const requiresApproval = requirement.deliverable?.requiresApproval;
  const isApproved = requirement.state === ContentState.ACCEPTED;
  return (
    isInstagramStories(postType ?? null) &&
    // Explicit false check: undefined falls through to the form page, which
    // handles every deliverable shape correctly. Treating undefined as "no
    // approval required" would risk bypassing the form for an incompletely
    // loaded deliverable.
    requiresApproval === false &&
    !isApproved &&
    requireInstagramInsights
  );
}

// ============================================================================
// Presentational View (exported for Storybook)
// ============================================================================

export interface SubmitContentPageViewProps {
  isLoading: boolean;
  error: Error | null;
  interactionNotFound: boolean;
  noSecureUrl: boolean;
  isBriefCanceled: boolean;
  outstanding: ContentRequirement[];
  completed: ContentRequirement[];
  extraContentItems: ExtraContent[];
  extraPostItems: ExtraPost[];
  formattedAgreementDate: string | null;
  orgSlug: string;
  profileSlug: string;
  campaignId: string;
  onOutstandingClick: (req: ContentRequirement) => void;
  onCompletedClick: (req: ContentRequirement) => void;
  viewingRequirement: ContentRequirement | null;
  onBackFromDetails: () => void;
  onEditFromDetails: () => void;
  onDeleteYouTubeVideoCollab?: (videoUrl: string) => void;
  isDeletingYouTubeCollab?: boolean;
  extraContentSlot?: ReactNode;
  usageRightsCustomValue?: number | null;
  usageRightsCustomUnit?: string | null;
  requireContentRights?: boolean;
}

export function SubmitContentPageView({
  isLoading,
  error,
  interactionNotFound,
  noSecureUrl,
  isBriefCanceled,
  outstanding,
  completed,
  extraContentItems,
  extraPostItems,
  formattedAgreementDate,
  orgSlug,
  profileSlug,
  campaignId,
  onOutstandingClick,
  onCompletedClick,
  viewingRequirement,
  onBackFromDetails,
  onEditFromDetails,
  onDeleteYouTubeVideoCollab,
  isDeletingYouTubeCollab,
  extraContentSlot,
  usageRightsCustomValue,
  usageRightsCustomUnit,
  requireContentRights,
}: SubmitContentPageViewProps): ReactNode {
  const totalCompletedCount =
    completed.length + extraContentItems.length + extraPostItems.length;

  if (isLoading) {
    return <SubmitContentSkeleton />;
  }

  if (interactionNotFound) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Interaction not found. It may have been removed or you don&apos;t
              have access.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link
                to="/o/$orgSlug/p/$profileSlug/c/$campaignId"
                params={{ orgSlug, profileSlug, campaignId }}
              >
                Back to Campaign
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (noSecureUrl) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Could not find brief information for this campaign. Please sign
              the brief first.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link
                to="/o/$orgSlug/p/$profileSlug/c/$campaignId"
                params={{ orgSlug, profileSlug, campaignId }}
              >
                Back to Campaign
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Brief has been cancelled — no submission allowed. Checked before the
  // generic `error` branch so creators see a clear cancellation message
  // instead of a generic failure even if a dependent query later errors.
  if (isBriefCanceled) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="font-medium">This brief has been cancelled.</p>
            <p className="text-sm text-muted-foreground mt-2">
              You can no longer submit content for this brief. Please reach out
              to the brand if you have any questions.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link
                to="/o/$orgSlug/p/$profileSlug/c/$campaignId"
                params={{ orgSlug, profileSlug, campaignId }}
              >
                Back to Campaign
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">
              Something went wrong loading content requirements. Please try
              again.
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link
                to="/o/$orgSlug/p/$profileSlug/c/$campaignId"
                params={{ orgSlug, profileSlug, campaignId }}
              >
                Back to Campaign
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (viewingRequirement) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6 overflow-x-hidden">
        <ContentDetailView
          requirement={viewingRequirement}
          onBack={onBackFromDetails}
          onEdit={onEditFromDetails}
          usageRightsDurationCustomValue={usageRightsCustomValue}
          usageRightsDurationCustomUnit={usageRightsCustomUnit}
          requireContentRights={requireContentRights}
          onDeleteYouTubeVideoCollab={onDeleteYouTubeVideoCollab}
          isDeletingYouTubeCollab={isDeletingYouTubeCollab}
        />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 overflow-x-hidden">
      <ProjectPageHeader
        title="Submit Content"
        subtitle={
          formattedAgreementDate && (
            <>For Brief signed on {formattedAgreementDate}</>
          )
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Outstanding Deliverables */}
        <Card className="overflow-hidden">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-semibold">
                Outstanding Deliverables
              </h3>
            </div>

            {outstanding.length === 0 ? (
              <div className="text-center py-8">
                <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  All deliverables completed!
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  You can still submit extra content if needed.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {outstanding.map((requirement) => (
                  <OutstandingDeliverableCard
                    key={requirement.id}
                    requirement={requirement}
                    onClick={() => onOutstandingClick(requirement)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Completed Deliverables */}
        <Card className="overflow-hidden">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Check className="h-5 w-5 text-green-500" />
              <h3 className="text-lg font-semibold">Completed Deliverables</h3>
              {totalCompletedCount > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({totalCompletedCount})
                </span>
              )}
            </div>

            {totalCompletedCount === 0 ? (
              <div className="text-center py-8">
                <FileImage className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  No completed deliverables yet.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Submit content to see it here.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {completed.map((requirement) => (
                  <CompletedDeliverableCard
                    key={requirement.id}
                    requirement={requirement}
                    onClick={() => onCompletedClick(requirement)}
                  />
                ))}
                {extraContentItems.map((item: ExtraContent) => (
                  <CompletedExtraContentCard
                    key={`extra-content-${item.id}`}
                    item={item}
                  />
                ))}
                {extraPostItems.map((item: ExtraPost) => (
                  <CompletedExtraPostCard
                    key={`extra-post-${item.id}`}
                    item={item}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {extraContentSlot}
    </div>
  );
}

// ============================================================================
// Container (hooks + data fetching)
// ============================================================================

function SubmitContentPage() {
  const { interactionId, orgSlug, profileSlug, campaignId } = Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { campaign, programId, profile, organization } = context;
  const navigate = useNavigate();

  // State for viewing content details (completed/pending deliverables)
  const [viewingRequirement, setViewingRequirement] =
    useState<ContentRequirement | null>(null);

  // State for Instagram Story Modal (story-first flow only)
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
  const [storyRequirement, setStoryRequirement] =
    useState<ContentRequirement | null>(null);

  // Get interactions for this project to find the secureUrl
  const { data: interactionsData, isPending: interactionsPending } =
    useProjectInteractionsQuery(organization.id, programId, profile.id);

  // Find the specific interaction by ID
  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );

  // Use the specific content interaction's secureUrl to get projectId and token.
  // Each SUBMIT_CONTENT interaction has its own secureUrl with the correct
  // externalPostProjectId and token for its associated brief/deliverables.
  const secureUrl = interaction?.secureUrl ?? "";

  // Extract token from secureUrl for Instagram stories API
  const { projectId: externalProjectId, token: secureToken } =
    extractProjectIdAndToken(secureUrl);
  const externalPostProjectId = externalProjectId
    ? parseInt(externalProjectId, 10)
    : NaN;

  // Get the Instagram account ID for stories (if available)
  // This comes from the story requirement's social accounts or deliverable account
  const igAccountId =
    storyRequirement?.socialAccounts?.find(
      (account) => account.networkIdentifier?.toLowerCase() === "instagram",
    )?.id ?? storyRequirement?.deliverable?.accountId;

  // Fetch Instagram stories when we have a story requirement with an account
  const { data: storiesData, isFetching: storiesFetching } =
    useInstagramStoriesQuery(
      organization.id,
      profile.id,
      igAccountId?.toString() ?? "",
      isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
      secureToken ?? "",
      false, // forceUpdate
    );

  // Hook for force-refreshing stories
  const refetchStories = useRefetchInstagramStories(
    organization.id,
    profile.id,
    igAccountId?.toString() ?? "",
    isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
    secureToken ?? "",
  );

  // Get content requirements using the secureUrl from the terms interaction
  const {
    data: contentData,
    isFetching: contentFetching,
    error: contentError,
  } = useContentRequirementsQuery(
    organization.id,
    secureUrl,
    profile.id,
    programId,
  );

  // Log content errors to console for debugging (without exposing details to users)
  useEffect(() => {
    if (contentError) console.error(contentError);
  }, [contentError]);

  const contentRequirements = contentData?.contentRequirements ?? [];
  const { outstanding, completed } =
    filterContentRequirements(contentRequirements);

  // Get agreement date from first content requirement
  const firstRequirement = first<ContentRequirement>(contentRequirements);
  const agreementDate = firstRequirement?.agreementDate;
  const formattedAgreementDate = agreementDate
    ? format(new Date(agreementDate * 1000), "MMM dd, yyyy")
    : null;

  // Get basic terms to access custom usage rights fields and cancellation state
  const { data: basicTermsData } = useBasicTermsQuery(
    organization.id,
    secureUrl,
  );
  const usageRightsCustomValue =
    basicTermsData?.terms?.usageRightsDurationCustomValue ?? null;
  const usageRightsCustomUnit =
    basicTermsData?.terms?.usageRightsDurationCustomUnit ?? null;
  // If the brief was cancelled, no submission should be allowed — this backs
  // up the task-tile gate in interaction-item.tsx and defends against
  // deep-link / email-reminder access paths (EPD-7798).
  const isBriefCanceled = basicTermsData?.terms?.canceled === true;

  // Get standard brief enhancements to get clientId for extra content
  const { data: briefEnhancementsData, isPending: briefEnhancementsPending } =
    useStandardBriefEnhancementsQuery(organization.id, secureUrl);
  const clientId = briefEnhancementsData?.enhancements?.clientId ?? "";
  const requireInstagramInsights =
    briefEnhancementsData?.enhancements?.requireInstagramInsights ?? true;
  // Whether the brief grants content usage rights. Passed to the content detail
  // view so the usage-rights card does not show "not granted" for briefs that
  // record rights as free-text legal language without a structured duration
  // (EPD-9453). Left tri-stated (boolean | undefined) — the page loading gate
  // below waits for this query so the card never renders before it resolves,
  // avoiding a flash of the "not granted" footer.
  const requireContentRights =
    briefEnhancementsData?.enhancements?.requireContentRights;

  // Fetch submitted extra content (uploaded files) and extra posts (live links)
  const { data: extraContentData } = useExtraContentQuery(
    organization.id,
    profile.id,
    isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
    secureToken ?? "",
  );
  const extraContentItems = extraContentData?.data ?? [];

  const { data: extraPostsData } = useExtraPostsQuery(
    organization.id,
    profile.id,
    clientId,
    isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
    secureToken ?? "",
  );
  const extraPostItems = extraPostsData ?? [];

  // Get Instagram account ID for extra content stories
  // Search all requirements for one whose socialAccounts includes an Instagram account
  const extraContentIgAccountId = useMemo(() => {
    for (const req of contentRequirements) {
      const igAccount = req.socialAccounts?.find(
        (account) => account.networkIdentifier?.toLowerCase() === "instagram",
      );
      if (igAccount?.id) return igAccount.id;
    }
    return firstRequirement?.deliverable?.accountId;
  }, [contentRequirements, firstRequirement]);

  // Fetch Instagram stories for extra content (reuse same data if available)
  const { data: extraStoriesData, isFetching: extraStoriesFetching } =
    useInstagramStoriesQuery(
      organization.id,
      profile.id,
      extraContentIgAccountId?.toString() ?? "",
      isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
      secureToken ?? "",
      false,
    );

  // Hook for force-refreshing extra content stories
  const refetchExtraStories = useRefetchInstagramStories(
    organization.id,
    profile.id,
    extraContentIgAccountId?.toString() ?? "",
    isNaN(externalPostProjectId) ? 0 : externalPostProjectId,
    secureToken ?? "",
  );

  // Query client for invalidating extra content queries
  const queryClient = useQueryClient();

  // Navigate to the content submission page for a given requirement
  const navigateToSubmit = useCallback(
    (requirement: ContentRequirement) => {
      navigate({
        to: "/o/$orgSlug/p/$profileSlug/c/$campaignId/submit-content/$interactionId/$contentReviewId",
        params: {
          orgSlug,
          profileSlug,
          campaignId,
          interactionId,
          contentReviewId: String(requirement.id),
        },
      });
    },
    [navigate, orgSlug, profileSlug, campaignId, interactionId],
  );

  // Handle successful extra content submission
  const handleExtraContentSuccess = useCallback(() => {
    captureEvent("extra_content_submitted", {
      organizationId: organization.id,
      interactionId,
    });
    // Invalidate extra content queries to refetch
    if (!isNaN(externalPostProjectId)) {
      queryClient.invalidateQueries({
        queryKey: extraContentKeys.extraContent(externalPostProjectId),
      });
      if (clientId) {
        queryClient.invalidateQueries({
          queryKey: extraContentKeys.extraPosts(
            clientId,
            externalPostProjectId,
          ),
        });
      }
    }
  }, [
    queryClient,
    externalPostProjectId,
    clientId,
    organization.id,
    interactionId,
  ]);

  // Handle updating extra content stories
  const handleUpdateExtraStories = useCallback(async () => {
    try {
      await refetchExtraStories();
      toast.success("Stories refreshed");
    } catch {
      toast.error("Failed to refresh stories");
    }
  }, [refetchExtraStories]);

  // Handle clicking on a deliverable - open story modal for stories, or navigate to submission page.
  // No-op when the brief is cancelled so the story picker can't be triggered
  // via any surviving code path.
  const handleDeliverableClick = useCallback(
    (requirement: ContentRequirement) => {
      if (isBriefCanceled) return;
      if (
        shouldOpenStoryPickerDirectly(requirement, requireInstagramInsights)
      ) {
        setStoryRequirement(requirement);
        setIsStoryModalOpen(true);
      } else {
        navigateToSubmit(requirement);
      }
    },
    [navigateToSubmit, requireInstagramInsights, isBriefCanceled],
  );

  // Handle story selection from the modal — navigate to submission page
  const handleSelectStories = useCallback(
    (_stories: InstagramStory[]) => {
      setIsStoryModalOpen(false);
      // Navigate to the submission page (stories will be re-fetched there)
      if (storyRequirement) {
        navigateToSubmit(storyRequirement);
      }
    },
    [storyRequirement, navigateToSubmit],
  );

  // Handle updating/refreshing stories
  const handleUpdateStories = useCallback(async () => {
    try {
      await refetchStories();
      toast.success("Stories refreshed");
    } catch {
      toast.error("Failed to refresh stories");
    }
  }, [refetchStories]);

  // Handle clicking on a completed deliverable to view details
  const handleViewDetails = useCallback((requirement: ContentRequirement) => {
    setViewingRequirement(requirement);
  }, []);

  // Handle going back from detail view to list view
  const handleBackFromDetails = useCallback(() => {
    setViewingRequirement(null);
  }, []);

  // Handle edit from detail view — navigate to submission page
  const handleEditFromDetails = useCallback(() => {
    if (viewingRequirement) {
      navigateToSubmit(viewingRequirement);
      setViewingRequirement(null);
    }
  }, [viewingRequirement, navigateToSubmit]);

  // Handle outstanding deliverable click — either view details or submit.
  // When the brief is cancelled, we short-circuit: the view renders a
  // cancellation notice so this handler shouldn't fire, but we guard here
  // too so nothing accidentally opens the submission modal or navigates.
  const handleOutstandingClick = useCallback(
    (requirement: ContentRequirement) => {
      if (isBriefCanceled) return;
      if (isAwaitingReview(requirement)) {
        setViewingRequirement(requirement);
      } else {
        handleDeliverableClick(requirement);
      }
    },
    [handleDeliverableClick, isBriefCanceled],
  );

  const agreementId = basicTermsData?.terms?.axAgreementId ?? "";

  const deleteYouTubeCollabMutation =
    useDeleteYouTubeBrandConnectVideoCollabMutation(
      organization.id,
      profile.id,
    );

  const handleDeleteYouTubeVideoCollab = useCallback(
    async (videoUrl: string) => {
      if (!agreementId || !clientId) return;
      try {
        await deleteYouTubeCollabMutation.mutateAsync({
          agreementId,
          clientId,
          videoUrl,
        });
        toast.success("YouTube video collab deleted");
        setViewingRequirement(null);
      } catch {
        toast.error("Failed to delete YouTube video collab");
      }
    },
    [agreementId, clientId, deleteYouTubeCollabMutation],
  );

  const isLoading =
    interactionsPending ||
    (!!secureUrl && (contentFetching || briefEnhancementsPending));

  return (
    <>
      <SubmitContentPageView
        isLoading={isLoading}
        error={contentError}
        interactionNotFound={!interactionsPending && !interaction}
        noSecureUrl={!isLoading && !secureUrl}
        isBriefCanceled={isBriefCanceled}
        outstanding={outstanding}
        completed={completed}
        extraContentItems={extraContentItems}
        extraPostItems={extraPostItems}
        formattedAgreementDate={formattedAgreementDate}
        orgSlug={organization.slug}
        profileSlug={campaign.profileSlug}
        campaignId={String(programId)}
        onOutstandingClick={handleOutstandingClick}
        onCompletedClick={handleViewDetails}
        viewingRequirement={viewingRequirement}
        onBackFromDetails={handleBackFromDetails}
        onEditFromDetails={handleEditFromDetails}
        onDeleteYouTubeVideoCollab={
          agreementId && clientId ? handleDeleteYouTubeVideoCollab : undefined
        }
        isDeletingYouTubeCollab={deleteYouTubeCollabMutation.isPending}
        usageRightsCustomValue={usageRightsCustomValue}
        usageRightsCustomUnit={usageRightsCustomUnit}
        requireContentRights={requireContentRights}
        extraContentSlot={
          !isNaN(externalPostProjectId) && secureToken ? (
            <div className="mt-6">
              <ExtraContentUpload
                organizationId={organization.id}
                profileId={profile.id}
                externalPostProjectId={externalPostProjectId}
                token={secureToken}
                clientId={clientId}
                igAccountId={extraContentIgAccountId?.toString()}
                stories={extraStoriesData?.stories ?? []}
                storiesLoading={extraStoriesFetching}
                onUpdateStories={handleUpdateExtraStories}
                onSuccess={handleExtraContentSuccess}
              />
            </div>
          ) : undefined
        }
      />

      {/* Instagram Story Selection Modal */}
      <InstagramStoryModal
        open={isStoryModalOpen}
        onOpenChange={setIsStoryModalOpen}
        stories={storiesData?.stories ?? []}
        loading={storiesFetching}
        brandInstagramUsername={storyRequirement?.brandInstagramUsername}
        onSelectStories={handleSelectStories}
        onUpdateClick={handleUpdateStories}
        insightsNotLinked={isInstagramInsightsNotLinked(
          storyRequirement?.socialAccounts,
        )}
        unlinkedAccountUsernames={getUnlinkedInstagramUsernames(
          storyRequirement?.socialAccounts,
        )}
      />
    </>
  );
}

// ============================================================================
// Due-date formatting helper
// ============================================================================

/**
 * Build a standardised due-date label from the structured fields returned by
 * the API.  Always prefixed with "Due" and uses the correct preposition
 * ("on" / "before" / "between") derived from the `type` field.
 *
 * "between" / "range" → MM/DD/YY – MM/DD/YY  (per AC #6)
 * all other types     → Month Day, Year       (per AC #1)
 */
function formatDueDateLabel(dueDate: {
  type?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  description?: string | null;
}): string {
  const { type, startDate, endDate } = dueDate;
  const typeLower = type?.toLowerCase() ?? "";

  if (typeLower === "between" || typeLower === "range") {
    const start = startDate ? format(new Date(startDate), "MM/dd/yy") : null;
    const end = endDate ? format(new Date(endDate), "MM/dd/yy") : null;
    if (start && end) return `Due between ${start} \u2013 ${end}`;
    if (end) return `Due before ${end}`;
    if (start) return `Due on ${start}`;
    return "Due";
  }

  if (typeLower === "before") {
    const dateStr = endDate || startDate;
    if (dateStr)
      return `Due before ${format(new Date(dateStr), "MMMM d, yyyy")}`;
    return "Due";
  }

  if (typeLower === "on") {
    const dateStr = startDate || endDate;
    if (dateStr) return `Due on ${format(new Date(dateStr), "MMMM d, yyyy")}`;
    return "Due";
  }

  // Unknown type — fallback
  const dateStr = endDate || startDate;
  if (dateStr) return `Due ${format(new Date(dateStr), "MMMM d, yyyy")}`;
  return dueDate.description ? `Due ${dueDate.description}` : "Due";
}

// ============================================================================
// Sub-components
// ============================================================================

interface OutstandingDeliverableCardProps {
  requirement: ContentRequirement;
  onClick?: () => void;
}

function OutstandingDeliverableCard({
  requirement,
  onClick,
}: OutstandingDeliverableCardProps) {
  const displayName = getContentDisplayName(requirement);
  const buttonTitle = getButtonTitle(requirement);
  const postType = requirement.deliverable?.postType;
  const postTypeDisplay = getPostTypeDisplayName(postType ?? null);
  const accountName = requirement.deliverable?.accountName;

  // Determine status badge
  const state = requirement.state;
  const isRejected = state === ContentState.REJECTED;
  const isPending =
    state === ContentState.UPLOADED ||
    state === ContentState.VALIDATING_UPLOAD ||
    state === ContentState.AMENDED ||
    state === ContentState.GCR_PENDING;
  const isApproved = state === ContentState.ACCEPTED;

  // Get due date info
  const dueDates = requirement.deliverable?.dueDates;
  const requiresApproval = requirement.deliverable?.requiresApproval;
  const relevantDueDate = requiresApproval
    ? dueDates?.submission
    : dueDates?.completion;
  const dueDateLabel = relevantDueDate
    ? formatDueDateLabel(relevantDueDate)
    : null;

  // Check if overdue
  const deadlineDate = relevantDueDate?.endDate || relevantDueDate?.startDate;
  const isOverdue = deadlineDate ? new Date(deadlineDate) < new Date() : false;

  return (
    <button
      type="button"
      className={`w-full text-left p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors overflow-hidden cursor-pointer ${
        isOverdue
          ? "border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20"
          : ""
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Content type and name */}
          <div className="flex items-center gap-2">
            {postType?.toLowerCase().includes("video") ? (
              <Video className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <Image className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span className="font-medium">{displayName}</span>
          </div>

          {/* Post type and account */}
          {(postType !== "FLEXIBLE" || accountName) && (
            <p className="text-sm text-muted-foreground mt-1 break-words">
              {postType !== "FLEXIBLE" && postTypeDisplay}
              {accountName && (
                <span>
                  {postType !== "FLEXIBLE" ? " on " : ""}@{accountName}
                </span>
              )}
            </p>
          )}

          {/* Due date */}
          {dueDateLabel && (
            <div
              className={`flex items-center gap-1 mt-2 text-xs ${
                isOverdue ? "text-red-600" : "text-muted-foreground"
              }`}
            >
              <Clock className="h-3 w-3 shrink-0" />
              <span>{dueDateLabel}</span>
            </div>
          )}

          {/* Status badges */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {isRejected && <ChangesRequestedBadge />}
            {isPending && <PendingApprovalBadge />}
            {isApproved && <ApprovedBadge />}
          </div>
        </div>

        {/* Action label */}
        <span className="shrink-0 whitespace-nowrap text-sm text-muted-foreground flex items-center">
          {buttonTitle}
          <ChevronRight className="h-4 w-4 ml-1" />
        </span>
      </div>

      {/* Rejection comment */}
      {isRejected && requirement.brandRejectionComment && (
        <div className="mt-3 p-2 bg-red-50 dark:bg-red-950/30 rounded text-sm">
          <p className="font-medium text-red-800 dark:text-red-200 mb-1">
            Brand Feedback:
          </p>
          <div
            className="text-red-700 dark:text-red-300"
            // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- sanitized with DOMPurify
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(requirement.brandRejectionComment),
            }}
          />
        </div>
      )}
    </button>
  );
}

// Shared thumbnail component with fallback for broken/missing images
interface DeliverableThumbnailProps {
  src: string | null;
  alt: string;
  fallbackIcon: ReactNode;
}

function DeliverableThumbnail({
  src,
  alt,
  fallbackIcon,
}: DeliverableThumbnailProps) {
  const [failed, setFailed] = useState(false);

  const handleError = (e: SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = "none";
    setFailed(true);
  };

  return (
    <div className="shrink-0 w-16 h-16 rounded overflow-hidden bg-muted">
      {src && !failed ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={handleError}
        />
      ) : null}
      {(!src || failed) && (
        <div className="w-full h-full flex items-center justify-center">
          {fallbackIcon}
        </div>
      )}
    </div>
  );
}

interface CompletedDeliverableCardProps {
  requirement: ContentRequirement;
  onClick?: () => void;
}

function CompletedDeliverableCard({
  requirement,
  onClick,
}: CompletedDeliverableCardProps) {
  const displayName = getContentDisplayName(requirement);
  const postType = requirement.deliverable?.postType;
  const postTypeDisplay = getPostTypeDisplayName(postType ?? null);
  const accountName = requirement.post?.username;

  // Get completion timestamp
  const completedTimestamp = requirement.completedDateTimeStamp;
  const completedDate = completedTimestamp
    ? format(new Date(completedTimestamp * 1000), "MMMM d, yyyy")
    : null;

  // Get preview image using the comprehensive media extraction.
  // Prefer image-type media; fall back to video thumbnail to avoid <img src> on a video URL.
  const mediaItems = extractContentMedia(requirement);
  const firstImage = mediaItems.find((item) => item.type === "image");
  const firstVideo = mediaItems.find((item) => item.type === "video");
  const previewImg = firstImage?.url ?? firstVideo?.thumbnailUrl ?? null;

  return (
    <button
      type="button"
      className="w-full text-left p-3 rounded-lg border bg-card overflow-hidden hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Preview thumbnail with fallback */}
        <DeliverableThumbnail
          src={previewImg}
          alt={displayName}
          fallbackIcon={<Image className="h-6 w-6 text-muted-foreground/50" />}
        />

        <div className="flex-1 min-w-0">
          {/* Content type and name */}
          <div className="flex items-center gap-2 flex-wrap">
            {postType?.toLowerCase().includes("video") ? (
              <Video className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <Image className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span className="font-medium truncate">{displayName}</span>
          </div>

          {/* Post type and account */}
          {(postType !== "FLEXIBLE" || accountName) && (
            <p className="text-sm text-muted-foreground mt-1">
              {postType !== "FLEXIBLE" && postTypeDisplay}
              {accountName && (
                <span>
                  {postType !== "FLEXIBLE" ? " by " : ""}@{accountName}
                </span>
              )}
            </p>
          )}

          {/* Completion date */}
          {completedDate && (
            <p className="text-xs text-muted-foreground mt-2">
              Completed {completedDate}
            </p>
          )}
        </div>

        {/* Status badge and chevron */}
        <div className="flex items-center gap-2 shrink-0">
          <CompletedDeliverableStatusBadge state={requirement.state} />
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </button>
  );
}

/**
 * Shows the appropriate status badge for a completed deliverable based on its state.
 * SOFT_APPROVED and PENDING_MARK_COMPLETE are treated as completed by the filter
 * but warrant a more specific badge in the UI.
 */
function CompletedDeliverableStatusBadge({ state }: { state: string }) {
  if (state === ContentState.SOFT_APPROVED) {
    return <ApprovedBadge />;
  }
  if (state === ContentState.PENDING_MARK_COMPLETE) {
    return <PendingApprovalBadge />;
  }
  return <CompletedBadge />;
}

interface CompletedExtraContentCardProps {
  item: ExtraContent;
}

function CompletedExtraContentCard({ item }: CompletedExtraContentCardProps) {
  const displayName = item.displayName || item.name || "Extra Content";
  const createdDate = item.createdTs
    ? format(new Date(item.createdTs * 1000), "MMM dd, yyyy")
    : null;

  // Get preview image from the item or its media
  const previewImg =
    item.image || item.media?.find((m: { url?: string | null }) => m.url)?.url;

  return (
    <div className="p-3 rounded-lg border bg-card overflow-hidden">
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Preview thumbnail with fallback */}
        <DeliverableThumbnail
          src={previewImg ?? null}
          alt={displayName}
          fallbackIcon={<Gift className="h-6 w-6 text-muted-foreground/50" />}
        />

        <div className="flex-1 min-w-0">
          {/* Content name */}
          <div className="flex items-center gap-2 flex-wrap">
            <Image className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium truncate">{displayName}</span>
          </div>

          {/* Content type */}
          <p className="text-sm text-muted-foreground mt-1">
            {item.contentType || "Uploaded File"}
          </p>

          {/* Created date */}
          {createdDate && (
            <p className="text-xs text-muted-foreground mt-2">
              Submitted {createdDate}
            </p>
          )}
        </div>

        {/* Extra Content badge */}
        <div className="flex items-center gap-2 shrink-0">
          <ExtraContentBadge />
        </div>
      </div>
    </div>
  );
}

interface CompletedExtraPostCardProps {
  item: ExtraPost;
}

function CompletedExtraPostCard({ item }: CompletedExtraPostCardProps) {
  const displayName = item.socialAccountUsername
    ? `@${item.socialAccountUsername}`
    : "Extra Post";

  const postedDate = item.datetimePosted
    ? format(new Date(item.datetimePosted), "MMM dd, yyyy")
    : null;

  // Get preview image
  const previewImg = item.image || item.imageUrl;

  const safeLink = (() => {
    if (!item.link) return null;
    try {
      const url = new URL(item.link);
      return ["http:", "https:"].includes(url.protocol) ? url.toString() : null;
    } catch {
      return null;
    }
  })();

  const cardContent = (
    <div className="flex items-start gap-2 sm:gap-3">
      {/* Preview thumbnail with fallback */}
      <DeliverableThumbnail
        src={previewImg ?? null}
        alt={displayName}
        fallbackIcon={<Link2 className="h-6 w-6 text-muted-foreground/50" />}
      />

      <div className="flex-1 min-w-0">
        {/* Post info */}
        <div className="flex items-center gap-2 flex-wrap">
          <Video className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium truncate">{displayName}</span>
        </div>

        {/* Network and post type */}
        <p className="text-sm text-muted-foreground mt-1">
          {item.network ? `${item.network} Post` : "Live Post"}
        </p>

        {/* Posted date */}
        {postedDate && (
          <p className="text-xs text-muted-foreground mt-2">
            Posted {postedDate}
          </p>
        )}
      </div>

      {/* Extra Content badge and link icon */}
      <div className="flex items-center gap-2 shrink-0">
        <ExtraContentBadge />
        {safeLink && <ExternalLink className="h-4 w-4 text-muted-foreground" />}
      </div>
    </div>
  );

  if (safeLink) {
    return (
      <a
        href={safeLink}
        target="_blank"
        rel="noopener noreferrer"
        className="block p-3 rounded-lg border bg-card overflow-hidden transition-colors hover:bg-accent/50"
      >
        {cardContent}
      </a>
    );
  }

  return (
    <div className="p-3 rounded-lg border bg-card overflow-hidden">
      {cardContent}
    </div>
  );
}

function SubmitContentSkeleton() {
  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Skeleton className="h-10 w-10 rounded-md" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Outstanding Deliverables */}
        <Card className="overflow-hidden">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-6 w-48" />
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-3 rounded-lg border bg-card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 rounded" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-3 w-40" />
                      <Skeleton className="h-5 w-24 rounded-full" />
                    </div>
                    <Skeleton className="h-8 w-20 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Completed Deliverables */}
        <Card className="overflow-hidden">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-6 w-44" />
            </div>
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="p-3 rounded-lg border bg-card">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-16 w-16 rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 rounded" />
                        <Skeleton className="h-4 w-28" />
                      </div>
                      <Skeleton className="h-3 w-36" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-5 w-14 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
